import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

interface CarouselItem {
  _id: string;
  title: string;
  description?: string;
  imageUrl: string;
  link?: string;
  order: number;
  isActive: boolean;
}

interface CarouselProps {
  items: CarouselItem[];
  autoPlay?: boolean;
  interval?: number;
  showIndicators?: boolean;
  showArrows?: boolean;
  height?: string;
}

const Carousel: React.FC<CarouselProps> = ({
  items,
  autoPlay = true,
  interval = 5000,
  showIndicators = true,
  showArrows = true,
  height = 'h-96'
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [loadedMap, setLoadedMap] = useState<Record<string, boolean>>({});
  const [errorMap, setErrorMap] = useState<Record<string, boolean>>({});

  const activeItems = items.filter(item => item.isActive).sort((a, b) => a.order - b.order);

  const nextSlide = useCallback(() => {
    if (activeItems.length <= 1) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev + 1) % activeItems.length);
  }, [activeItems.length]);

  const prevSlide = useCallback(() => {
    if (activeItems.length <= 1) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev - 1 + activeItems.length) % activeItems.length);
  }, [activeItems.length]);

  const goToSlide = useCallback((index: number) => {
    if (index === currentIndex) return;
    setIsTransitioning(true);
    setCurrentIndex(index);
  }, [currentIndex]);

  useEffect(() => {
    if (activeItems.length === 0) return;

    if (autoPlay) {
      const timer = setInterval(() => {
        nextSlide();
      }, interval);

      return () => clearInterval(timer);
    }
  }, [autoPlay, interval, nextSlide, activeItems.length]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsTransitioning(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [currentIndex]);

  // 只渲染当前页和相邻页，避免一次拉取全部超大原图
  const shouldRender = (index: number) => {
    if (activeItems.length <= 3) return true;
    const prev = (currentIndex - 1 + activeItems.length) % activeItems.length;
    const next = (currentIndex + 1) % activeItems.length;
    return index === currentIndex || index === prev || index === next;
  };

  if (activeItems.length === 0) {
    return (
      <div className={`${height} bg-gray-100 flex items-center justify-center`}>
        <div className="text-center text-gray-500">
          <div className="text-4xl mb-2">📸</div>
          <p>暂无团队活动</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden rounded-lg bg-gray-900 ${height}`}>
      <div className="relative w-full h-full">
        {activeItems.map((item, index) => {
          const isCurrent = index === currentIndex;
          const render = shouldRender(index);
          const loaded = !!loadedMap[item._id];
          const failed = !!errorMap[item._id];

          return (
            <div
              key={item._id}
              className={`absolute inset-0 transition-opacity duration-300 ${
                isCurrent ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
              }`}
              aria-hidden={!isCurrent}
            >
              {render && !failed && (
                <img
                  src={item.imageUrl}
                  alt={item.title}
                  decoding="async"
                  loading={isCurrent ? 'eager' : 'lazy'}
                  className={`w-full h-full object-contain transition-opacity duration-300 ${
                    loaded ? 'opacity-100' : 'opacity-0'
                  }`}
                  onLoad={() =>
                    setLoadedMap((prev) => (prev[item._id] ? prev : { ...prev, [item._id]: true }))
                  }
                  onError={() =>
                    setErrorMap((prev) => (prev[item._id] ? prev : { ...prev, [item._id]: true }))
                  }
                />
              )}

              {isCurrent && !loaded && !failed && (
                <div className="absolute inset-0 flex items-center justify-center text-gray-300 text-sm">
                  图片加载中…
                </div>
              )}

              {isCurrent && failed && (
                <div className="absolute inset-0 flex items-center justify-center text-gray-300 text-sm">
                  图片加载失败
                </div>
              )}

              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center text-white px-6 pointer-events-auto">
                  {item.description && (
                    <p className="text-lg mb-6 max-w-2xl mx-auto">{item.description}</p>
                  )}
                  {item.link && (
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block bg-white text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                    >
                      了解更多
                    </a>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {showArrows && activeItems.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            disabled={isTransitioning}
            className="absolute left-4 top-1/2 z-20 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-colors disabled:opacity-50"
          >
            <ChevronLeftIcon className="h-6 w-6" />
          </button>
          <button
            onClick={nextSlide}
            disabled={isTransitioning}
            className="absolute right-4 top-1/2 z-20 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-colors disabled:opacity-50"
          >
            <ChevronRightIcon className="h-6 w-6" />
          </button>
        </>
      )}

      {showIndicators && activeItems.length > 1 && (
        <div className="absolute bottom-4 left-1/2 z-20 transform -translate-x-1/2 flex space-x-2">
          {activeItems.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-colors ${
                index === currentIndex
                  ? 'bg-white'
                  : 'bg-white bg-opacity-50 hover:bg-opacity-75'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Carousel;
